import { useCallback, useEffect, useRef, useState } from 'react';
import { QrCode } from '../QrCode';
import { CheckCircle, Clock, ScanLine } from '../icons';
import { qrLoginApi } from '../../services/qrLogin';
import { useAuth } from '../../contexts/AuthContext';

type Phase = 'loading' | 'waiting' | 'scanned' | 'expired' | 'error';

export function ScanLoginPanel({ active, onSuccess }: { active: boolean; onSuccess: () => void }) {
  const { login } = useAuth();
  const [qrId, setQrId] = useState<string | null>(null);
  const [pollToken, setPollToken] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const timer = useRef<number | null>(null);

  const createSession = useCallback(async () => {
    setPhase('loading');
    setQrId(null);
    setPollToken(null);
    try {
      const r = await qrLoginApi.create();
      setQrId(r.qrId);
      setPollToken(r.pollToken);
      setPhase('waiting');
    } catch {
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    if (active) createSession();
  }, [active, createSession]);

  useEffect(() => {
    if (!active || !qrId || !pollToken || phase === 'expired' || phase === 'error' || phase === 'loading') return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const r = await qrLoginApi.status(qrId, pollToken);
        if (r.status === 'scanned') {
          setPhase('scanned');
        } else if (r.status === 'consumed' && r.accessToken && r.user) {
          login(r.user, r.accessToken);
          onSuccess();
          return;
        } else if (r.status === 'expired' || r.status === 'rejected') {
          setPhase('expired');
          return;
        }
      } catch {
        // transient network error: keep polling
      }
      timer.current = window.setTimeout(tick, 1500);
    };
    timer.current = window.setTimeout(tick, 1500);
    return () => {
      stopped = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [active, qrId, pollToken, phase, login, onSuccess]);

  const qrValue = qrId ? `${window.location.origin}/scan-login?qid=${qrId}` : '';

  return (
    <div className="flex h-full flex-col items-center text-center">
      <h3 className="text-base font-semibold text-fg">扫码登录</h3>
      <p className="mt-1 flex flex-wrap items-center justify-center gap-1 text-sm text-fg-subtle">
        手机上已登录本站，点顶栏的
        <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-line text-fg-muted">
          <ScanLine className="h-3.5 w-3.5" />
        </span>
        扫一扫
      </p>
      <div className="mt-5 flex min-h-[196px] flex-col items-center justify-center gap-3">
        {phase === 'waiting' && <QrCode value={qrValue} size={180} />}

        {phase === 'loading' && <span className="text-sm text-fg-subtle">生成中…</span>}

        {phase === 'scanned' && (
          <>
            <CheckCircle className="h-16 w-16 text-success" />
            <span className="text-sm font-medium text-fg">已扫描，请在手机上确认</span>
          </>
        )}

        {(phase === 'expired' || phase === 'error') && (
          <>
            <Clock className="h-16 w-16 text-fg-subtle" />
            <span className="text-sm text-fg-subtle">{phase === 'expired' ? '二维码已过期' : '生成失败'}</span>
            <button
              type="button"
              onClick={createSession}
              className="rounded bg-navy-fill px-3 py-1.5 text-sm text-white hover:bg-navy-fill-strong max-lg:rounded-control"
            >
              刷新二维码
            </button>
          </>
        )}
      </div>
    </div>
  );
}
